import React from "react";
import { View, ActivityIndicator } from "react-native";
import Text from "app/components/Text";
import TextInput from "app/components/TextInput";
import TouchableOpacity from "app/components/TouchableOpacity";
import store from "app/redux/store";
import valueActions from "app/utils/valueActions";
import colorHelpers from "app/utils/colorHelpers";
import { StyleContext } from "app/contexts/Style";

// Shown on the home screen while offline (or while offline-created notes are
// still waiting to sync). Lets a note be captured even when no chapter data
// could be fetched: the note becomes a value queued through the offline
// queue, pinned server-side to the day it was written once back online.
export default class OfflineNoteCard extends React.Component {
  static contextType = StyleContext;

  state = {
    isOnline: true,
    text: "",
    pendingNotes: [],
    saving: false,
  };

  componentDidMount() {
    this.syncFromStore();
    this.unsubscribe = store.subscribe(this.syncFromStore);
  }

  componentWillUnmount() {
    this.unsubscribe && this.unsubscribe();
  }

  syncFromStore = () => {
    let { app, values } = store.getState();
    let pendingNotes = (values || []).filter((v) => !!v.pending);
    this.setState({ isOnline: app.isOnline, pendingNotes });
  };

  handleSave = async () => {
    let { text, saving } = this.state;
    if (saving || !text?.trim()?.length) return;
    this.setState({ saving: true });
    let lines = text.trim().split(/[\r\n]/);
    let title = lines[0];
    let body = lines.slice(1).join("\n").trim();
    // no chapter_id: the server pins the value to the day chapter containing
    // its created_at, so late syncs still land on the right day
    await valueActions.create({ title, text: body });
    this.setState({ saving: false, text: "" });
  };

  render() {
    let { isOnline, text, pendingNotes, saving } = this.state;
    if (isOnline !== false && !pendingNotes.length) return null;
    let { colors, sizes } = this.context;

    return (
      <View
        style={{
          backgroundColor: colorHelpers.hexToRgbaString(colors.trim, 0.2),
          borderRadius: sizes.borderRadius,
          padding: sizes.base,
          marginBottom: sizes.base,
          maxWidth: 600,
          width: "100%",
          alignSelf: "center",
        }}
      >
        <Text medium>
          {isOnline === false ? "You're offline" : "Syncing notes…"}
        </Text>
        <Text trim style={{ fontSize: sizes.small * 2, marginTop: 2 }}>
          Notes saved here will sync to the day they were written.
        </Text>

        {isOnline === false && (
          <View style={{ marginTop: sizes.base }}>
            <TextInput
              multiline
              autoHeight
              value={text}
              onChangeText={(text) => this.setState({ text })}
              placeholder="Jot a note..."
              style={{
                backgroundColor: colorHelpers.hexToRgbaString(
                  colors.empty,
                  0.6,
                ),
                borderRadius: sizes.borderRadius,
                padding: sizes.small,
                minHeight: sizes.default * 1.6 * 2,
              }}
            />
            <View
              style={{
                flexDirection: "row",
                justifyContent: "flex-end",
                marginTop: sizes.small,
              }}
            >
              {saving && (
                <ActivityIndicator
                  size="small"
                  color={colors.foreground}
                  style={{ marginRight: sizes.small }}
                />
              )}
              <Text
                smallButton
                onPress={this.handleSave}
                style={{ opacity: !!text?.trim()?.length ? 1 : 0.4 }}
              >
                Save note
              </Text>
            </View>
          </View>
        )}

        {!!pendingNotes.length && (
          <View style={{ marginTop: sizes.small }}>
            {pendingNotes.map((note) => (
              <View
                key={note.id}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  paddingVertical: 4,
                }}
              >
                <View
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: colors.trim,
                    marginRight: sizes.small,
                  }}
                />
                <Text numberOfLines={1} style={{ flex: 1 }}>
                  {note.title || note.text}
                </Text>
                <Text trim style={{ fontSize: sizes.small * 2 }}>
                  pending
                </Text>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  }
}
